import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { CheckCircle2, Package, Truck, CreditCard, MapPin, User, ArrowLeft } from "lucide-react";

import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { ordersApi } from "@/services/api";
import type { Address, OrderDetail } from "@/services/types/domain";
import { formatPrice, orderVariant, paymentVariant, tOrderStatus, tPaymentStatus, parseAddress } from "@/utils/formatters";
//import { toast } from "react-hot-toast";

// Tipos locales para timeline (tolerante a backend)
type TimelineEvent = {
    status: string;          // p.ej. created | payment_completed | processing | shipped | delivered | cancelled | refund_requested | refund_approved | refund_rejected
    note?: string | null;
    timestamp: string;      // ISO o "YYYY-MM-DD HH:mm:ss"
};

// Estado de reembolso en la orden (si lo envía el backend)
type RefundStatus = "pending" | "approved" | "rejected" | null | undefined;
const tRefundStatus = (s: RefundStatus) =>
    !s ? "Sin solicitud" : s === "pending" ? "Reembolso pendiente" : s === "approved" ? "Reembolso aprobado" : "Reembolso rechazado";
const refundVariant = (s: RefundStatus) =>
    s === "pending" ? "warning" : s === "approved" ? "success" : s === "rejected" ? "destructive" : "outline";

const OrderConfirmationPage: React.FC = () => {
    const navigate = useNavigate();
    const { orderId } = useParams<{ orderId: string }>();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [order, setOrder] = useState<OrderDetail | null>(null);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                // El servicio ya devuelve response.data.data → objeto limpio
                const data = await ordersApi.getOrderById(Number(orderId));
                if (!mounted) return;
                setOrder(data as OrderDetail);
            } catch (err: any) {
                if (!mounted) return;
                const msg = err?.response?.data?.message || err?.message || "No se pudo obtener la orden";
                setError(msg);
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [orderId]);

    // Direcciones (acepta string JSON o objeto Address)
    const shippingAddr = useMemo(() => (order?.shipping_address ? (parseAddress(order.shipping_address) as Address) : null), [order]);
    const billingAddr = useMemo(() => (order?.billing_address ? (parseAddress(order.billing_address) as Address) : null), [order]);

    // Timeline: aceptamos `order.events` o `order.tracking`
    const timeline: TimelineEvent[] = useMemo(() => {
        const raw = (order as any)?.events ?? (order as any)?.tracking ?? [];
        return Array.isArray(raw) ? raw.slice().sort((a: any, b: any) => +new Date(a.created_at) - +new Date(b.created_at)) : [];
    }, [order]);

    const refundStatus: RefundStatus = (order as any)?.refund_request_status;
    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-gray-50">
                <div className="animate-pulse text-gray-500">Cargando confirmación…</div>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="min-h-screen bg-gray-50 px-4 py-8">
                <div className="mx-auto max-w-4xl">
                    <div className="mb-4">
                        <Link to="/orders" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Volver a mis pedidos
                        </Link>
                    </div>
                    <div className="rounded-2xl border bg-white p-6 text-red-600">{error || "No se encontró la orden"}</div>
                </div>
            </div>
        );
    }

    const currency = order.currency || "PEN";
    const items = (order as any).items ?? []; // items de la orden

    const disaggregate = (amount: number, igvRate = 0.18) => {
        const base = +(amount / (1 + igvRate)).toFixed(2);
        const igv = +(amount - base).toFixed(2);
        return { base, igv, total: amount };
    };
    const product = disaggregate(Number(order.subtotal || 0));
    const shipping = disaggregate(Number(order.shipping_amount || 0));
    const total = {
        base: +(product.base + shipping.base).toFixed(2),
        igv: +(product.igv + shipping.igv).toFixed(2),
        total: +(product.total + shipping.total).toFixed(2),
    };

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-8">
            <div className="mx-auto w-full max-w-6xl">
                {/* Header */}
                <div className="mb-6 rounded-2xl border bg-white p-5 sm:p-6 shadow-sm">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                            <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                                <CheckCircle2 className="h-6 w-6 text-green-600" />
                            </div>
                            <div>
                                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                                    ¡Gracias! Tu pedido está confirmado
                                </h1>
                                <p className="text-sm text-gray-600">
                                    Pedido <span className="font-medium">{order.order_number || `#${order.id}`}</span>
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                            Pedido <Badge variant={orderVariant(order.status)}>{tOrderStatus(order.status)}</Badge>
                            Pago <Badge variant={paymentVariant(order.payment_status)}>{tPaymentStatus(order.payment_status)}</Badge>
                            {typeof refundStatus !== "undefined" && (
                                <Badge variant={refundVariant(refundStatus)}>{tRefundStatus(refundStatus)}</Badge>
                            )}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                    {/* Columna izquierda: artículos + timeline */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Artículos */}
                        <section className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                                <Package className="w-5 h-5" /> Artículos
                            </div>

                            {/* Head desktop */}
                            <div className="hidden md:grid grid-cols-12 gap-2 px-2 pb-2 text-xs font-semibold text-gray-500">
                                <div className="col-span-6">Producto</div>
                                <div className="col-span-2 text-right">Precio</div>
                                <div className="col-span-2 text-right">Cantidad</div>
                                <div className="col-span-2 text-right">Total</div>
                            </div>

                            {/* Rows */}
                            <div className="divide-y">
                                {items.map((it: any) => (
                                    <div key={it.id} className="grid grid-cols-1 gap-2 px-2 py-3 md:grid-cols-12 md:items-center">
                                        <div className="md:col-span-6">
                                            <div className="font-medium text-gray-900">{it.product_name || it.name}</div>
                                            {it.product_sku && <div className="text-xs text-gray-500">SKU: {it.product_sku}</div>}
                                            {it.store_name && <div className="text-xs text-gray-500">Tienda: {it.store_name}</div>}
                                        </div>
                                        <div className="md:col-span-2 text-right text-sm text-gray-700">{formatPrice(Number(it.price || 0), currency)}</div>
                                        <div className="md:col-span-2 text-right text-sm text-gray-700">{it.quantity}</div>
                                        <div className="md:col-span-2 text-right text-sm font-semibold text-gray-900">{formatPrice(Number(it.total || 0), currency)}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Totales */}
                            {/* <div className="mt-4 flex flex-col items-end gap-1 text-sm">
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6">
                                    <span className="text-gray-600">Subtotal</span>
                                    <span className="font-medium">{formatPrice(Number(order.subtotal || 0), currency)}</span>
                                </div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6">
                                    <span className="text-gray-600">Impuestos</span>
                                    <span className="font-medium">{formatPrice(Number(order.tax_amount || 0), currency)}</span>
                                </div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6">
                                    <span className="text-gray-600">Envío</span>
                                    <span className="font-medium">{formatPrice(Number(order.shipping_amount || 0), currency)}</span>
                                </div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6">
                                    <span className="text-gray-600">Descuento</span>
                                    <span className="font-medium">- {formatPrice(Number(order.discount_amount || 0), currency)}</span>
                                </div>
                                <div className="flex w-full md:w-auto justify-between md:justify-end gap-6 text-base">
                                    <span className="font-semibold text-gray-900">Total</span>
                                    <span className="font-bold">{formatPrice(Number(order.total_amount || 0), currency)}</span>
                                </div>
                            </div> */}
                            <div className="mt-4 flex flex-col items-end gap-1 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Productos (incluye IGV)</span>
                                    <span className="font-medium">{formatPrice(Number(total.total || 0), currency)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Transporte / Envío (incluye IGV)</span>
                                    <span className="font-medium">{formatPrice(Number(order.shipping_amount || 0), currency)}</span>
                                </div>
                                <div className="border-t border-gray-300 my-2"></div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Base imponible (sin IGV)</span>
                                    <span className="font-medium">{formatPrice(Number(total.base || 0), currency)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">IGV (18%)</span>
                                    <span className="font-medium">{formatPrice(Number(total.igv || 0), currency)}</span>
                                </div>
                                <div className="flex justify-between text-lg font-semibold">
                                    <span>Total a pagar</span>
                                    <span className="font-medium">{formatPrice(Number(order.total_amount || 0), currency)}</span>
                                </div>
                            </div>


                        </section>

                        {/* Timeline / Seguimiento */}
                        {(timeline?.length || 0) > 0 && (
                            <section className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                                <div className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                                    <Truck className="w-5 h-5" /> Seguimiento
                                </div>
                                <div className="space-y-4">
                                    {timeline.map((ev, idx) => (
                                        <div key={idx} className="flex items-start justify-between gap-3">
                                            <div>
                                                <div className="font-medium text-gray-900">{tOrderStatus(ev.status as any) || ev.status}</div>
                                                {ev.note && <div className="text-sm text-gray-600">{ev.note}</div>}
                                            </div>
                                            <div className="text-xs text-gray-500">{new Date(ev.timestamp).toLocaleString()}</div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>

                    {/* Columna derecha: direcciones + pago + acciones */}
                    <aside className="space-y-6">
                        {/* Envío */}
                        <section className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                                <MapPin className="w-5 h-5" /> Dirección de envío
                            </div>
                            <div className="text-sm text-gray-700">
                                {shippingAddr ? (
                                    <>
                                        <div className="font-medium">{shippingAddr.recipient_name}</div>
                                        <div>{shippingAddr.address_line_1}{shippingAddr.address_line_2 ? `, ${shippingAddr.address_line_2}` : ""}</div>
                                        <div>{shippingAddr.city}, {shippingAddr.state} {shippingAddr.postal_code}</div>
                                        <div>{shippingAddr.country}</div>
                                        {shippingAddr.phone && <div className="text-gray-500 mt-1">{shippingAddr.phone}</div>}
                                    </>
                                ) : (
                                    <div className="text-gray-500">—</div>
                                )}
                            </div>
                        </section>

                        {/* Facturación */}
                        <section className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                                <User className="w-5 h-5" /> Dirección de facturación
                            </div>
                            <div className="text-sm text-gray-700">
                                {billingAddr ? (
                                    <>
                                        <div className="font-medium">{billingAddr.recipient_name}</div>
                                        <div>{billingAddr.address_line_1}{billingAddr.address_line_2 ? `, ${billingAddr.address_line_2}` : ""}</div>
                                        <div>{billingAddr.city}, {billingAddr.state} {billingAddr.postal_code}</div>
                                        <div>{billingAddr.country}</div>
                                        {billingAddr.phone && <div className="text-gray-500 mt-1">{billingAddr.phone}</div>}
                                    </>
                                ) : (
                                    <div className="text-gray-500">—</div>
                                )}
                            </div>
                        </section>

                        {/* Pago */}
                        <section className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                                <CreditCard className="w-5 h-5" /> Pago
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-sm">
                                <span className="text-gray-700">Estado:</span>
                                <Badge variant={paymentVariant(order.payment_status)}>{tPaymentStatus(order.payment_status)}</Badge>
                            </div>
                            <p className="mt-2 text-xs text-gray-500">
                                Pedido creado el {order.created_at ? new Date(order.created_at).toLocaleString() : "—"}.
                            </p>
                        </section>

                        {/* Acciones */}
                        <section className="rounded-2xl border bg-white p-4 sm:p-6 shadow-sm">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <Button onClick={() => navigate("/", { replace: true })} className="w-full">Ir al inicio</Button>
                                <Button onClick={() => navigate("/orders")} className="w-full">Ver mis pedidos</Button>
                            </div>

                            {/* Info reembolso para evitar duplicados */}
                            {typeof refundStatus !== "undefined" && (
                                <div className="mt-4">
                                    <div className="text-sm text-gray-700 mb-1">Estado de reembolso</div>
                                    <Badge variant={refundVariant(refundStatus)}>{tRefundStatus(refundStatus)}</Badge>
                                    {refundStatus === "pending" && (
                                        <p className="mt-1 text-xs text-amber-600">
                                            Ya existe una solicitud de reembolso pendiente para este pedido.
                                        </p>
                                    )}
                                </div>
                            )}
                        </section>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default OrderConfirmationPage;
